"use client";

import React, { useRef, useState, useEffect } from "react";
import Button from "@components/atoms/Button";
import styles from "./Carousel.module.scss";
import Text from "@components/atoms/Text";
import Image from "@components/atoms/Image";
import assetLoader from "@utils/internalImageLoder";

interface CarouselProps {
  children: React.ReactNode[];
  className?: string;
  heading?: string;
  headingClassName?: string;
  leftArrowPath?: string;
  rightArrowPath?: string;
  arrowButtonClass?: string;
  carouselWrapperClassName?: string;
  headingWrapperClassName?: string;
  carouselItemClassName?: string;
}

export const Carousel = ({
  children,
  className = "",
  heading = "",
  headingClassName = "",
  leftArrowPath = `${assetLoader()}/icons/Carousal/ArrowLeft.svg`,
  rightArrowPath = `${assetLoader()}/icons/Carousal/ArrowRight.svg`,
  arrowButtonClass = "",
  carouselWrapperClassName = "",
  headingWrapperClassName = "",
  carouselItemClassName = "",
}: CarouselProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDesktop, setIsDesktop] = useState(true);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const handleResize = () => setIsDesktop(mediaQuery.matches);

    setIsDesktop(mediaQuery.matches);
    mediaQuery.addEventListener("change", handleResize);

    return () => mediaQuery.removeEventListener("change", handleResize);
  }, []);

  const updateScrollIndicators = () => {
    if (!isDesktop) return;
    const container = containerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    setIsScrollable(scrollWidth > clientWidth);
    setIsAtStart(Math.round(scrollLeft) <= 5);
    setIsAtEnd(Math.round(scrollLeft + clientWidth) >= Math.round(scrollWidth));
  };

  const scroll = (direction: "left" | "right") => {
    if (!isDesktop) return; // skip on mobile
    const container = containerRef.current;
    if (!container) return;

    const scrollAmount = container.offsetWidth * 0.9;
    container.scrollBy({
      left: direction === "right" ? scrollAmount : -scrollAmount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isDesktop) return;

    updateScrollIndicators();
    container.addEventListener("scroll", updateScrollIndicators);

    return () => {
      container.removeEventListener("scroll", updateScrollIndicators);
    };
  }, [children.length, isDesktop]);

  return (
    <div className={`${styles.carouselWrapper} ${carouselWrapperClassName}`}>
      <div className={`${styles.headingWrapper} ${headingWrapperClassName}`}>
        <Text level="h2" className={`${styles.heading} ${headingClassName}`}>
          {heading}
        </Text>
        {isDesktop && isScrollable && (
          <div className={styles.controls}>
            <Button
              onClick={() => scroll("left")}
              className={`${styles.arrowButton} ${arrowButtonClass}`}
              aria-label="Scroll left"
              disabled={isAtStart}
            >
              <Image
                src={leftArrowPath}
                alt={"Left Arrow"}
                className={styles.navIcon}
                width={20}
                height={20}
              />
            </Button>
            <Button
              onClick={() => scroll("right")}
              className={`${styles.arrowButton} ${arrowButtonClass}`}
              aria-label="Scroll right"
              disabled={isAtEnd}
            >
              <Image
                src={rightArrowPath}
                alt={"Right Arrow"}
                className={styles.navIcon}
                width={20}
                height={20}
              />
            </Button>
          </div>
        )}
      </div>

      <div
        ref={containerRef}
        className={`${styles.carouselContainer} ${className} ${
          isDesktop ? styles.snap : styles.mobileScroll
        }`}
      >
        {children.map((child, index) => (
          <div
            key={index}
            className={`${styles.carouselItem} ${carouselItemClassName}`}
          >
            <div className={styles.carouselItemInner}>{child}</div>
          </div>
        ))}
      </div>
    </div>
  );
};
